import React, { useState, useMemo, ChangeEvent } from 'react';
import { X, Download } from 'lucide-react';
import { Modal } from 'common/components/Modal';
import { Button, Select } from 'common/components';
import { uploadContacts } from 'services/api/ContactServiceAPI';
import { UploadContactsResponse } from 'types/contact.type';
import { useGetWorkflowConfig } from 'services/hooks/queries';
import { buildCsvSchema, downloadCsvTemplate } from 'utils/csvSchema.utils';
import { UploadContactsModalProps } from './UploadContactsModal.types';
import styles from './UploadContactsModal.module.scss';

const UploadContactsModal: React.FC<UploadContactsModalProps> = ({
    isOpen,
    onClose,
    workflows,
    onUploadSuccess,
    onUploadError,
}) => {
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(
        workflows.length > 0 ? workflows[0].workflowId : ''
    );

    const { workflowConfig, isLoadingWorkflowConfig } = useGetWorkflowConfig(selectedWorkflowId);
    const schema = useMemo(
        () => buildCsvSchema(workflowConfig?.config?.leadFields),
        [workflowConfig?.config?.leadFields]
    );

    // Response modal state
    const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
    const [invalidPhones, setInvalidPhones] = useState<string[]>([]);
    const [duplicatePhones, setDuplicatePhones] = useState<string[]>([]);
    const [skippedContacts, setSkippedContacts] = useState<{ phoneNumber: string; missingFields: string[] }[]>([]);

    // Handle file selection
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setUploadFile(e.target.files[0]);
        }
    };

    // Clear the selected file
    const clearFileSelection = () => {
        setUploadFile(null);
        const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Handle workflow selection
    const handleWorkflowChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedWorkflowId(e.target.value);
    };

    // Trigger file input click
    const triggerFileInput = () => {
        const input = document.getElementById('csv-file-input') as HTMLInputElement;
        if (input) {
            input.click();
        }
    };

    // Close the main upload modal
    const handleClose = () => {
        if (!isUploading) {
            setUploadFile(null);
            if (workflows.length > 0) {
                setSelectedWorkflowId(workflows[0].workflowId);
            }
            onClose();
        }
    };

    // Close the response modal
    const closeResponseModal = () => {
        setShowResponseModal(false);
        setInvalidPhones([]);
        setDuplicatePhones([]);
        setSkippedContacts([]);
    };

    const handleDownloadTemplate = (): void => downloadCsvTemplate(schema);

    // Handle file upload
    const handleUploadFile = async () => {
        if (!uploadFile) {
            onUploadError('Please select a CSV file to upload.');
            return;
        }

        if (!selectedWorkflowId) {
            onUploadError('Please select a workflow.');
            return;
        }

        try {
            setIsUploading(true);
            const fileReader = new FileReader();

            fileReader.onload = async (e) => {
                const csvText = e.target?.result as string;

                let parsed: ReturnType<typeof schema.parseCSV>;
                try {
                    parsed = schema.parseCSV(csvText);
                } catch (parseErr) {
                    onUploadError('Could not parse CSV file: ' + ((parseErr as Error).message || 'Unknown error'));
                    setIsUploading(false);
                    return;
                }

                const { contacts, invalidPhoneCount, invalidPhonesData, missingHeaders, jsonErrors } = parsed;

                if (missingHeaders.length) {
                    onUploadError(`CSV is missing required headers: ${missingHeaders.join(', ')}`);
                    setIsUploading(false);
                    return;
                }

                if (jsonErrors.length) {
                    const rows = jsonErrors.map((err) => `row ${err.row} (${err.column})`).join(', ');
                    onUploadError(`Found ${jsonErrors.length} row(s) with invalid JSON: ${rows}`);
                    setIsUploading(false);
                    return;
                }

                if (invalidPhoneCount > 0 && contacts.length === 0) {
                    onUploadError('No valid contacts found in the CSV file.');
                    setIsUploading(false);
                    return;
                }

                try {
                    const response: UploadContactsResponse = await uploadContacts({
                        contacts,
                        workflowId: selectedWorkflowId,
                    });

                    const clientInvalidPhones = invalidPhonesData.map((data) => data.phoneNumber);
                    const mergedInvalidPhones = Array.from(
                        new Set([...(response.invalidPhones || []), ...clientInvalidPhones])
                    );
                    const duplicates = response.duplicatePhones || [];
                    const skipped = response.skippedMissingRequiredFields || [];

                    if (mergedInvalidPhones.length || duplicates.length || skipped.length) {
                        setInvalidPhones(mergedInvalidPhones);
                        setDuplicatePhones(duplicates);
                        setSkippedContacts(skipped);

                        handleClose();
                        setShowResponseModal(true);
                    } else {
                        handleClose();
                        onUploadSuccess();
                    }
                } catch (error) {
                    console.error('Error uploading file:', error);

                    const errorMsg =
                        (error as any).response?.data?.error ||
                        (error as Error).message ||
                        'Unknown error occurred while uploading contacts';

                    onUploadError(errorMsg);
                } finally {
                    setIsUploading(false);
                }
            };

            fileReader.onerror = () => {
                onUploadError('Error reading file.');
                setIsUploading(false);
            };

            fileReader.readAsText(uploadFile);
        } catch (error) {
            console.error('Error reading file:', error);
            onUploadError('Error reading file: ' + ((error as Error).message || 'Unknown error'));
            setIsUploading(false);
        }
    };

    return (
        <>
            {/* Upload Modal for CSV Upload */}
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title='Upload Contacts'
                size='md'
                showDefaultFooter={false}
                closeOnOverlayClick={!isUploading}
                closeOnEscape={!isUploading}
            >
                <div className={styles.modalBody}>
                    <p className={styles.modalText}>
                        Required columns: <strong>{schema.requiredColumns.join(', ')}</strong>
                    </p>
                    <p className={styles.modalText}>
                        Optional columns: <strong>{schema.optionalColumns.join(', ')}</strong>
                    </p>
                    {schema.leadFields.length > 0 && (
                        <div className={styles.customFields}>
                            <p className={styles.modalText}>
                                <strong>Custom fields:</strong>
                            </p>
                            <ul>
                                {schema.leadFields.map((field) => (
                                    <li key={field.key}>
                                        <strong>{field.label || field.key}</strong>
                                        {field.required ? ' (required)' : ' (optional)'}
                                        {field.description && <>: {field.description}</>}
                                        {field.example && <> Example: {field.example}</>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className={styles.fileInputContainer}>
                        <input
                            type='file'
                            id='csv-file-input'
                            accept='.csv'
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant='outline-primary'
                            onClick={triggerFileInput}
                            className={styles.fileButton}
                            type='button'
                        >
                            Choose CSV File
                        </Button>
                        <div className={styles.fileNameContainer}>
                            <span className={styles.fileName}>{uploadFile ? uploadFile.name : 'No file selected'}</span>
                            {uploadFile && (
                                <Button
                                    variant='ghost'
                                    size='sm'
                                    icon={X}
                                    iconOnly
                                    onClick={clearFileSelection}
                                    className={styles.clearFileIcon}
                                    type='button'
                                    aria-label='Clear file selection'
                                />
                            )}
                        </div>
                        <Button
                            variant='outline-primary'
                            onClick={handleDownloadTemplate}
                            className={styles.fileButton}
                            type='button'
                            icon={Download}
                            disabled={isLoadingWorkflowConfig}
                        >
                            Download CSV template
                        </Button>
                    </div>

                    <div className={styles.workflowSelector}>
                        <label htmlFor='workflow-select'>Assign to Workflow:</label>
                        <Select
                            value={selectedWorkflowId}
                            onChange={handleWorkflowChange}
                            options={workflows.map((workflow) => ({
                                value: workflow.workflowId,
                                label: workflow.workflowName,
                            }))}
                        />
                    </div>

                    <div className={styles.modalFooter}>
                        <Button variant='outline-secondary' onClick={handleClose} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button
                            variant='primary'
                            onClick={handleUploadFile}
                            disabled={isUploading || isLoadingWorkflowConfig}
                        >
                            {isUploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Upload Response Modal for Invalid, Duplicate and Skipped Phones */}
            <Modal
                isOpen={showResponseModal}
                onClose={closeResponseModal}
                title='Upload Results'
                size='md'
                showDefaultFooter={false}
            >
                <div className={styles.modalBody}>
                    {invalidPhones.length > 0 || duplicatePhones.length > 0 || skippedContacts.length > 0 ? (
                        <div>
                            {invalidPhones.length > 0 && (
                                <>
                                    <p className={styles.modalText}>
                                        The following phone numbers are <strong>invalid</strong> and were not uploaded:
                                    </p>
                                    <ul className={styles.invalidPhonesList}>
                                        {invalidPhones.map((phone, index) => (
                                            <li key={`invalid-${index}`} className={styles.invalidPhone}>
                                                {phone}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className={styles.phoneFormatGuidance}>
                                        <p>Make sure your phone numbers follow the proper format:</p>
                                        <ul>
                                            <li>Include country code (e.g., +1 for US)</li>
                                            <li>Use only numbers, no spaces or special characters</li>
                                            <li>
                                                Example of valid format: <code>+15551234567</code>
                                            </li>
                                        </ul>
                                    </div>
                                </>
                            )}

                            {duplicatePhones.length > 0 && (
                                <>
                                    <p className={styles.modalText}>
                                        The following phone numbers are <strong>duplicates</strong> and were not
                                        uploaded:
                                    </p>
                                    <ul className={styles.invalidPhonesList}>
                                        {duplicatePhones.map((phone, index) => (
                                            <li key={`duplicate-${index}`} className={styles.duplicatePhone}>
                                                <span className={styles.duplicateLabel}>Duplicate</span> {phone}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className={styles.modalText}>
                                        <small>
                                            These numbers already exist in the system and will not be added again.
                                        </small>
                                    </p>
                                </>
                            )}

                            {skippedContacts.length > 0 && (
                                <>
                                    <p className={styles.modalText}>
                                        The following contacts were <strong>skipped</strong> because required fields for
                                        this workflow were missing:
                                    </p>
                                    <ul className={styles.invalidPhonesList}>
                                        {skippedContacts.map((item, index) => (
                                            <li key={`skipped-${index}`} className={styles.skippedPhone}>
                                                {item.phoneNumber} — missing: {item.missingFields.join(', ')}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {(invalidPhones.length > 0 || duplicatePhones.length > 0) && (
                                <p className={styles.modalText}>
                                    Please correct these phone numbers and try uploading them again.
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className={styles.modalText}>All contacts were uploaded successfully.</p>
                    )}

                    <div className={styles.modalFooter}>
                        <Button variant='outline-secondary' onClick={closeResponseModal}>
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default UploadContactsModal;
